import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  DollarSign,
  Clock,
  MessageCircle,
  Moon,
  Users,
  Lock,
  ArrowRight,
  ChevronRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { apiClient } from '@/services/api/apiClient';
import { useNavigate } from 'react-router-dom';

interface DineAIImpactProps {
  plan: string;
}

interface ImpactData {
  summary: {
    revenue_generated: number;
    total_orders: number;
    avg_order_value: number;
    questions_answered: number;
    staff_hours_saved: number;
  };
  details?: {
    upsell_success_rate: number;
    dietary_concerns_handled: number;
    complex_modifications: number;
    wine_pairings_accepted: number;
    missed_calls_recovered: number;
    avg_order_time_minutes: number;
    phone_order_time_minutes: number;
  };
  trends?: {
    week_over_week_growth: number;
    returning_customer_rate: number;
    peak_ordering_hours: string[];
    most_active_day: string;
  };
  date_range?: {
    start: string;
    end: string;
  };
  upgrade_prompt?: string;
}

const DineAIImpactCardHorizontal = ({ plan }: DineAIImpactProps) => {
  const navigate = useNavigate();
  const [impactData, setImpactData] = useState<ImpactData | null>(null);
  const [questionsData, setQuestionsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImpactData();
  }, []);

  const fetchImpactData = async () => {
    try {
      console.log('🔍 Fetching DINE AI impact data from cache...');
      // Use the cached endpoint to avoid real-time queries
      const params = new URLSearchParams({
        menu_type: 'regular'
      });
      const response = await apiClient.get(`/analytics/dine-ai-impact-cached?${params}`);
      console.log('📊 DINE AI cached impact response:', response.data);

      // Set the impact data directly from the response
      if (response.data) {
        setImpactData(response.data);

        // Fetch questions for the same date range as the impact data
        if (response.data.date_range) {
          fetchQuestionsAnswered(response.data.date_range);
        } else {
          fetchQuestionsAnswered();
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch impact data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionsAnswered = async (dateRange?: any) => {
    try {
      console.log('🔍 Fetching questions answered data...');
      let url = '/analytics/questions-answered';

      // If we have date range from impact data, use it
      if (dateRange && dateRange.start && dateRange.end) {
        url += `?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      } else {
        // Fallback to last 7 days
        url += '?days=7';
      }

      const response = await apiClient.get(url);
      console.log('💬 Questions answered response:', response.data);

      if (response.data) {
        setQuestionsData(response.data);
      }
    } catch (error) {
      console.error('❌ Failed to fetch questions data:', error);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-4 gap-4">
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!impactData) {
    return null;
  }

  // Check if we have any data
  const hasData = impactData.summary && impactData.summary.total_orders > 0;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    return new Intl.NumberFormat('en-US').format(value);
  };

  const isPlanYummy = plan.toLowerCase() === 'yummy';
  const isPlanDelicious = plan.toLowerCase() === 'delicious';
  const isPlanGourmet = plan.toLowerCase() === 'gourmet';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 }}
      className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all"
    >
      {/* Header with Sparkle Effect */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <motion.div
            className="p-3 bg-yellow-100 rounded-xl relative"
            whileHover={{ scale: 1.05 }}
          >
            <TrendingUp className="h-6 w-6 text-yellow-600" />
            <motion.div
              className="absolute -top-1 -right-1"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="h-4 w-4 text-yellow-500" />
            </motion.div>
          </motion.div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Dine AI Impact Dashboard</h3>
            <p className="text-gray-500 text-sm">
              {impactData.date_range ? (
                <>
                  Last Refresh: {new Date(impactData.date_range.start + 'T12:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })} - {new Date(impactData.date_range.end + 'T12:00:00').toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </>
              ) : (
                'Last 7 days'
              )} • {formatNumber(impactData.summary.total_orders)} orders captured
            </p>
          </div>
        </div>
        {/* Menu Type Badge */}
        <div className="px-4 py-1.5 bg-blue-100 rounded-full border border-blue-200">
          <span className="text-blue-700 text-xs font-semibold">Regular Menu</span>
        </div>
      </div>

      {/* Main Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {/* Revenue Generated */}
        <motion.div
          className="bg-green-50 rounded-xl p-4 border border-green-200"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {formatCurrency(impactData.summary.revenue_generated)}
          </p>
          <p className="text-xs text-gray-500">Revenue Generated</p>
        </motion.div>

        {/* Orders Captured */}
        <motion.div
          className="bg-blue-50 rounded-xl p-4 border border-blue-200"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="flex items-center justify-between mb-2">
            <Users className="h-5 w-5 text-blue-600" />
            <span className="text-xs text-blue-600 font-medium">24/7</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {formatNumber(impactData.summary.total_orders)}
          </p>
          <p className="text-xs text-gray-500">Orders Captured</p>
        </motion.div>

        {/* Average Order Value */}
        <motion.div
          className="bg-purple-50 rounded-xl p-4 border border-purple-200"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            <span className="text-xs text-purple-600 font-medium">per table</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {formatCurrency(impactData.summary.avg_order_value)}
          </p>
          <p className="text-xs text-gray-500">Avg Order Value</p>
        </motion.div>

        {/* Staff Hours Saved */}
        <motion.div
          className="bg-orange-50 rounded-xl p-4 border border-orange-200"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <div className="flex items-center justify-between mb-2">
            <Clock className="h-5 w-5 text-orange-600" />
            <span className="text-xs text-orange-600 font-medium">3 min/order</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-1">
            {impactData.summary.staff_hours_saved} hrs
          </p>
          <p className="text-xs text-gray-500">Server Time Saved</p>
        </motion.div>
      </div>

      {/* Success Banner */}
      <div className="bg-green-50 rounded-xl p-3 border border-green-200 mb-6">
        <div className="flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-green-700 text-sm font-medium">
              Zero Order Errors with Dine AI
            </p>
            <p className="text-green-600 text-xs mt-0.5">
              {formatNumber(questionsData?.questions_answered || 0)} customer questions answered instantly while dining
            </p>
          </div>
        </div>
      </div>

      {/* View Full Analytics Button */}
      <div className="flex justify-end">
        <button
          onClick={() => navigate('/analytics')}
          className="px-4 py-2 bg-[#FDB913] text-black rounded-lg hover:bg-[#e5a711] transition-all font-medium text-sm inline-flex items-center gap-2"
        >
          View Full Analytics
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default DineAIImpactCardHorizontal;
